import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LinkIcon from "@mui/icons-material/Link";

import { renderBetaInviteEmail } from "./betaInviteTemplate";

/**
 * SCK-619 / SCK-738 — copy-once fallback modal for the token-invite
 * response shape landed in SCK-617, with SCK-738 adding a branded
 * "Email" tab so operators can paste a ready-made beta-invite into
 * Gmail / Outlook instead of a bare URL.
 *
 * When SES email delivery is disabled (Phase 1) or the invitation
 * response carries `deliveryMode === "copy_only"`, coord returns a
 * one-time `redemptionUrl` that the inviter must hand to the invitee
 * out of band. This modal surfaces that URL with two paths:
 *
 *   - **Link** tab: single-click copy of the raw redemption URL.
 *   - **Email** tab: subject + preheader + a full branded email body,
 *     copyable as rich HTML (ClipboardItem text/html + text/plain) for
 *     Gmail-style compose, or as plain text for text-only clients.
 *
 * Rendered by the parent of both MemberFormDialog and
 * InviteToProjectDialog so the same UX applies to every mint path.
 *
 * Props:
 *   open         boolean
 *   url          string — the redemption URL to copy
 *   email        string — invitee address, shown for context + used in template
 *   expiresOn?   string — ISO timestamp; rendered as "Expires <iso>" when set
 *   onClose()    close the modal (parent may also refresh here)
 */
export default function CopyLinkModal({ open, url, email, expiresOn, onClose }) {
  const [tab, setTab] = useState(0);
  const [copiedKey, setCopiedKey] = useState(null);

  const invite = useMemo(
    () => renderBetaInviteEmail({ redemptionUrl: url, email, expiresOn }),
    [url, email, expiresOn],
  );

  const flash = (key) => {
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((prev) => (prev === key ? null : prev)), 1500);
  };

  const copyText = async (key, text) => {
    try {
      await navigator.clipboard?.writeText(text || "");
      flash(key);
    } catch {
      // Clipboard denied (permissions, insecure context). The textarea /
      // input is still selectable so the operator can copy manually.
    }
  };

  // Rich-text copy. Writes both text/html AND text/plain so Gmail /
  // Apple Mail compose renders the formatted email on paste, while a
  // plain-text target (Markdown editor, terminal) still gets usable
  // content. Falls back to text-only when ClipboardItem is unavailable.
  const copyRich = async (key, html, text) => {
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard?.writeText(text || "");
      }
      flash(key);
    } catch {
      // noop — ask the operator to use the plain-text button
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="copy-link-modal-title"
    >
      <DialogTitle id="copy-link-modal-title">Invitation ready to share</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Email delivery is off, so no message was sent to{" "}
          <strong>{email || "the invitee"}</strong>. Copy the link or paste the
          branded email below into Gmail / Outlook.
        </Alert>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          aria-label="Share invitation as link or email"
          sx={{ mb: 2 }}
        >
          <Tab icon={<LinkIcon />} iconPosition="start" label="Link" data-testid="tab-link" />
          <Tab icon={<EmailOutlinedIcon />} iconPosition="start" label="Email" data-testid="tab-email" />
        </Tabs>

        {tab === 0 && (
          <Stack spacing={2}>
            <TextField
              fullWidth
              value={url || ""}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copiedKey === "link" ? "Copied" : "Copy link"}>
                      <IconButton
                        onClick={() => copyText("link", url || "")}
                        edge="end"
                        aria-label="Copy redemption link"
                        data-testid="copy-link-button"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                "aria-label": "Redemption URL",
                onFocus: (e) => e.target.select(),
                "data-testid": "redemption-url-input",
              }}
            />
            {expiresOn && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Link expires {expiresOn}.
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              This link is shown once. If you lose it, revoke the invitation and issue a new one.
            </Typography>
          </Stack>
        )}

        {tab === 1 && (
          <Stack spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Subject"
              value={invite.subject}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={copiedKey === "subject" ? "Copied" : "Copy subject"}>
                      <IconButton
                        onClick={() => copyText("subject", invite.subject)}
                        edge="end"
                        aria-label="Copy subject line"
                        data-testid="copy-subject-button"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              inputProps={{ onFocus: (e) => e.target.select(), "data-testid": "subject-input" }}
            />
            <TextField
              fullWidth
              size="small"
              label="Preheader (shown next to the subject in inbox previews)"
              value={invite.preheader}
              InputProps={{ readOnly: true }}
              inputProps={{ onFocus: (e) => e.target.select() }}
            />

            <Box
              role="region"
              aria-label="Email preview"
              data-testid="email-preview"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                maxHeight: 420,
                overflowY: "auto",
                bgcolor: "background.default",
              }}
              dangerouslySetInnerHTML={{ __html: invite.html }}
            />

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={() => copyRich("rich", invite.html, invite.text)}
                data-testid="copy-rich-button"
              >
                {copiedKey === "rich" ? "Copied" : "Copy as rich text"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={() => copyText("plain", invite.text)}
                data-testid="copy-plain-button"
              >
                {copiedKey === "plain" ? "Copied" : "Copy as plain text"}
              </Button>
            </Stack>

            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Paste the rich-text copy into a Gmail or Apple Mail compose window; both the subject
              and body render with the brand styling. The plain-text copy mirrors §17 of the brand
              kit — use it for text-only mail clients, or send it as the <code>multipart/alternative</code>{" "}
              text part.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {tab === 0 ? (
          <Button
            variant="contained"
            onClick={() => copyText("link", url || "")}
            startIcon={<ContentCopyIcon />}
          >
            {copiedKey === "link" ? "Copied" : "Copy link"}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => copyRich("rich", invite.html, invite.text)}
            startIcon={<ContentCopyIcon />}
          >
            {copiedKey === "rich" ? "Copied" : "Copy as rich text"}
          </Button>
        )}
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
